import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import QRCode from 'qrcode';

interface QRCodeDisplayProps {
  sessionId: string;
  sessionCode: string;
  mosId: string;
  size?: number;
  className?: string;
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  sessionId,
  sessionCode,
  mosId,
  size = 200,
  className = '',
}) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const generateQR = async () => {
      try {
        const shareUrl = `${window.location.origin}/result?id=${sessionId}&mos=${mosId}`;

        const dataUrl = await QRCode.toDataURL(shareUrl, {
          width: size,
          margin: 2,
          color: {
            dark: '#D4AF37', // Army gold
            light: '#0A0A0A', // Dark background
          },
          errorCorrectionLevel: 'M',
        });

        setQrCodeUrl(dataUrl);
        setError(null);
      } catch (err) {
        console.error('QR code generation error:', err);
        setError('Failed to generate QR code');
      }
    };

    if (sessionId && mosId) {
      generateQR();
    }
  }, [sessionId, mosId, size]);

  if (error) {
    return (
      <div className={`flex flex-col items-center ${className}`}>
        <div className="text-red-500 text-sm">{error}</div>
      </div>
    );
  }

  if (!qrCodeUrl) {
    return (
      <div className={`flex flex-col items-center ${className}`}>
        <div
          className="bg-army-dark animate-pulse"
          style={{ width: size, height: size }}
        />
      </div>
    );
  }

  return (
    <motion.div
      className={`flex flex-col items-center ${className}`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* QR Code Container */}
      <div className="relative">
        {/* Outer glow effect */}
        <div className="absolute inset-0 bg-army-gold/20 blur-xl rounded-lg" />

        {/* QR Code with border */}
        <div className="relative border-2 4k:border-4 border-army-gold rounded-lg overflow-hidden bg-army-black p-2 4k:p-4">
          <img
            src={qrCodeUrl}
            alt="Scan to download your photos"
            className="block"
            style={{ width: size, height: size }}
          />

          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-4 h-4 4k:w-8 4k:h-8 border-t-2 border-l-2 4k:border-t-4 4k:border-l-4 border-army-gold" />
          <div className="absolute top-0 right-0 w-4 h-4 4k:w-8 4k:h-8 border-t-2 border-r-2 4k:border-t-4 4k:border-r-4 border-army-gold" />
          <div className="absolute bottom-0 left-0 w-4 h-4 4k:w-8 4k:h-8 border-b-2 border-l-2 4k:border-b-4 4k:border-l-4 border-army-gold" />
          <div className="absolute bottom-0 right-0 w-4 h-4 4k:w-8 4k:h-8 border-b-2 border-r-2 4k:border-b-4 4k:border-r-4 border-army-gold" />
        </div>
      </div>

      {/* Session Code */}
      <div className="mt-2 4k:mt-6 text-center">
        <p className="text-xs 4k:text-3xl text-gray-400 uppercase tracking-wider mb-1 4k:mb-2">
          Session Code
        </p>
        <p className="text-lg 4k:text-5xl font-mono font-bold text-army-gold tracking-widest">
          {sessionCode}
        </p>
      </div>

      {/* Instructions */}
      <p className="mt-2 4k:mt-4 text-xs 4k:text-2xl text-gray-400 text-center max-w-[200px] 4k:max-w-[400px]">
        Scan QR code to download your photos on your phone
      </p>
    </motion.div>
  );
};

export default QRCodeDisplay;
